import { InkPaletteCard } from "./InkPalettes";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Loader2, Sparkles, ShieldAlert, AlertTriangle, CheckCircle2, RotateCcw, Upload } from "lucide-react";
import { productionOf, type ApparelStudy, type StudyLayer, type StudyColor } from "@erc/shared";
import { customApi, type CustomListingDetail } from "../../lib/customListing";
import { InspirationPanel } from "./InspirationPanel";

// Custom Liste Studyosu 2. adim: AI liste calismasi. Sonuc Etsy'ye bakan bir
// duzende gosterilir: katmanlar, tisort tonuna gore renkler, ilhamdaki urunler,
// alicinin gorecegi sorular. Onay sonraki adimlari acar; duzeltme notu ders olur.

const KIND_LABEL: Record<StudyLayer["kind"], string> = {
  fixed_art: "Sabit çizim", font_text: "Font yazısı", glyph_text: "Harf harf çizim", graphic_slot: "Grafik yuvası", photo_slot: "Fotoğraf yuvası",
};
const SOURCE_LABEL: Record<StudyLayer["source"], string> = {
  fixed: "Sabit", buyer_text: "Alıcı yazar", buyer_pick: "Alıcı seçer", buyer_upload: "Alıcı yükler",
};
const SIDE_LABEL: Record<StudyLayer["side"], string> = { front: "Ön", back: "Arka", sleeve: "Kol", pocket: "Cep", other: "Diğer" };
const COLOR_RULE_LABEL: Record<StudyLayer["colorRule"], string> = { fixed: "Sabit renk", by_tone: "Tişört tonuna göre", buyer_pick: "Alıcı seçer" };
const TONE_LABEL = { light: "Açık tişört", mid: "Orta ton", dark: "Koyu tişört" } as const;
const TYPE_LABEL: Record<ApparelStudy["listingTypes"][number], string> = {
  fixed_text: "Sabit tasarım + metin", glyph_text: "Harf çizimi", menu_pick: "Menüden seçim", design_set: "Tasarım seti", photo: "Fotoğraflı", blank_canvas: "Boş tuval",
};
const RISK_TONE = { low: "bg-emerald-50 text-emerald-700", medium: "bg-amber-50 text-amber-700", high: "bg-red-50 text-red-600" } as const;
const RISK_LABEL = { low: "Telif riski düşük", medium: "Telif riski orta", high: "Telif riski yüksek" } as const;

function Swatch({ c }: { c?: StudyColor }) {
  if (!c) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
      <span className="h-3.5 w-3.5 rounded-full border border-gray-300" style={{ background: c.hex ?? "transparent" }} />
      {c.name}
    </span>
  );
}

export function StudyStep({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const images = rec.study?.imageUrls?.length ? rec.study.imageUrls : (rec.source.imageUrls ?? []);
  return (
    <>
      <InspirationPanel images={images} url={rec.analysis?.url} title={rec.name} />
      <StudyBody rec={rec} onChanged={onChanged} />
    </>
  );
}

function StudyBody({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const study = rec.study;
  const [model, setModel] = useState(rec.defaultModel);
  const [feedback, setFeedback] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (fb?: string) => customApi("POST", `/${rec.id}/study`, { model, ...(fb ? { feedback: fb } : {}) }),
    onSuccess: () => { setErr(null); setFeedback(""); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const approve = useMutation({
    mutationFn: (status: "approved" | "draft") => customApi("PATCH", `/${rec.id}/study`, { status }),
    onSuccess: onChanged,
    onError: (e) => setErr((e as Error).message),
  });
  const setProduction = useMutation({
    mutationFn: (production: "template" | "ai" | "auto") => customApi("PATCH", `/${rec.id}/study`, { production }),
    onSuccess: onChanged,
    onError: (e) => setErr((e as Error).message),
  });
  const picked = rec.source.imageUrls?.length ?? 0;

  const runner = (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor="study-model" className="sr-only">Model</label>
      <select id="study-model" value={model} onChange={(e) => setModel(e.target.value)}
        className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
        {rec.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>
      <button onClick={() => run.mutate(undefined)} disabled={run.isPending || study?.status === "running"}
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
        {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {study?.layers ? "Baştan çalıştır" : "Liste çalışmasını başlat"}
      </button>
      <span className="text-xs text-gray-500">{picked || 1} fotoğraf gönderilir · 1-3 dakika sürer</span>
    </div>
  );

  if (!study || (!study.layers && study.status !== "running")) {
    return (
      <div className="space-y-3">
        {study?.status === "failed" && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{study.error ?? "Çalışma başarısız oldu."}</p>}
        <p className="text-sm text-gray-600">Kaynak adımında seçtiğin fotoğraflar, ilham listesinin başlığı, açıklaması ve Etsy kişiselleştirme soruları AI'a gönderilir.</p>
        {runner}
        {err && <p className="text-sm text-red-600">{err}</p>}
      </div>
    );
  }

  if (study.status === "running") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-700">
        <Loader2 className="h-4 w-4 animate-spin" /> AI ilham listesini inceliyor. Bu sayfa kendiliğinden yenilenir; başka işe geçebilirsin.
      </div>
    );
  }

  // Buradan sonra calisma tamam: katmanlar ve diger alanlar dolu
  const full = study as ApparelStudy;
  const approved = full.status === "approved";
  const byTone = full.layers.filter((l) => l.colorRule === "by_tone");
  const buyerLayers = full.layers.filter((l) => l.source !== "fixed");

  return (
    <div className="space-y-6">
      {full.status === "failed" && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">Son çalışma başarısız: {full.error}. Aşağıda önceki sonuç duruyor.</p>}

      {/* Ozet + etiketler */}
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {full.listingTypes.map((t) => <span key={t} className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{TYPE_LABEL[t]}</span>)}
          <span className={clsx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", RISK_TONE[full.risk.level])}><ShieldAlert className="h-3 w-3" />{RISK_LABEL[full.risk.level]}</span>
          {!full.printable.ok && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">Baskıyla üretilemez</span>}
          {full.ownBackground && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Kendi zemini var</span>}
        </div>
        <p className="text-sm text-gray-700">{full.summaryTr}</p>
        {(() => {
          // Uretim yolu: kuraldan (decideProduction) ya da senin secimin
          const p = productionOf(full);
          return (
            <div className={clsx("mt-3 flex flex-wrap items-center gap-2 rounded-lg border p-3 text-sm", p.mode === "ai" ? "border-violet-200 bg-violet-50/60" : "border-emerald-200 bg-emerald-50/60")}>
              <span className={clsx("rounded-full px-2 py-0.5 text-xs font-semibold", p.mode === "ai" ? "bg-violet-100 text-violet-800" : "bg-emerald-100 text-emerald-800")}>
                Üretim yolu: {p.mode === "ai" ? "AI" : "Şablon"}{p.override ? " (senin seçimin)" : ""}
              </span>
              <span className="min-w-0 flex-1 text-xs text-gray-700">
                {p.mode === "ai" ? "3. adım atlanır; örnekler 4. adımda ve sipariş tasarımı AI ile çizilir. " : "3. adımda şablon kurulur; örnekler ve sipariş kodla basılır. "}{p.reasonTr}
              </span>
              <button onClick={() => setProduction.mutate(p.mode === "ai" ? "template" : "ai")} disabled={setProduction.isPending}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {p.mode === "ai" ? "Şablon yoluna geç" : "AI yoluna geç"}
              </button>
              {p.override && <button onClick={() => setProduction.mutate("auto")} className="text-xs text-gray-500 hover:underline">Kurala dön</button>}
            </div>
          );
        })()}
        {full.layers.some((l) => l.colorRule === "buyer_pick") && <div className="mt-2"><InkPaletteCard rec={rec} onChanged={onChanged} /></div>}
        {(full.risk.level !== "low" && (full.risk.reasonsTr.length > 0 || full.risk.alternativeTr)) && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
            {full.risk.reasonsTr.map((r) => <p key={r}>{r}</p>)}
            {full.risk.alternativeTr && <p className="mt-1"><span className="font-semibold">Risksiz alternatif:</span> {full.risk.alternativeTr}</p>}
          </div>
        )}
        {!full.printable.ok && full.printable.noteTr && <p className="mt-2 text-sm text-red-600">{full.printable.noteTr}</p>}
      </div>

      {/* Katmanlar */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700">Tasarımın katmanları</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr><th className="px-3 py-2">Katman</th><th className="px-3 py-2">Tür</th><th className="px-3 py-2">Kim belirler</th><th className="px-3 py-2">Yer</th><th className="px-3 py-2">İlhamdaki değer</th><th className="px-3 py-2">Renk</th></tr>
            </thead>
            <tbody>
              {full.layers.map((l) => (
                <tr key={l.key} className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900">{l.labelTr}</span>
                    {l.anchorTo && <span className="block text-xs text-gray-500">{full.layers.find((x) => x.key === l.anchorTo)?.labelTr ?? l.anchorTo} katmanına bağlı</span>}
                    {l.noteTr && <span className="mt-0.5 block max-w-md text-xs text-gray-500">{l.noteTr}</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{KIND_LABEL[l.kind]}</td>
                  <td className="px-3 py-2 text-gray-700">{SOURCE_LABEL[l.source]}</td>
                  <td className="px-3 py-2 text-gray-700">{SIDE_LABEL[l.side]}</td>
                  <td className="px-3 py-2">
                    {l.sampleValue ? <span className="text-gray-900">{l.sampleValue}</span> : <span className="text-gray-400">—</span>}
                    {l.isPlaceholder && <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">yer tutucu</span>}
                    {l.format && (
                      <span className="block text-xs text-gray-500">
                        {[l.format.prefix && `ön ek "${l.format.prefix}"`, l.format.suffix && `son ek "${l.format.suffix}"`, l.format.separator && `ayraç "${l.format.separator}"`,
                          l.format.maxChars && `en fazla ${l.format.maxChars} karakter`, l.format.optional && "isteğe bağlı", l.format.emptyBehaviorTr].filter(Boolean).join(" · ")}
                      </span>
                    )}
                    {l.options?.length ? <span className="block text-xs text-gray-500">Seçenekler: {l.options.join(", ")}</span> : null}
                  </td>
                  <td className="px-3 py-2">
                    <span className="block text-xs text-gray-500">{COLOR_RULE_LABEL[l.colorRule]}</span>
                    {l.colorRule === "fixed" && <Swatch c={l.fixedColor} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tona gore renkler */}
      {rec.isGarment !== false && byTone.length > 0 && full.palettes.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-semibold text-gray-700">Tişört tonuna göre renkler</h3>
          <p className="mb-2 text-xs text-gray-500">Varyasyon şablonundaki her tişört rengi açık, orta ya da koyu gruba düşer; o gruptaki renkler basılır.</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr><th className="px-3 py-2">Katman</th>{full.palettes.map((p) => <th key={p.tone} className="px-3 py-2">{TONE_LABEL[p.tone]}</th>)}</tr>
              </thead>
              <tbody>
                {byTone.map((l) => (
                  <tr key={l.key} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-900">{l.labelTr}</td>
                    {full.palettes.map((p) => <td key={p.tone} className="px-3 py-2"><Swatch c={p.colors[l.key]} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ilhamdaki urunler */}
      {full.items.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-semibold text-gray-700">İlham fotoğrafındaki ürünler</h3>
          <p className="mb-2 text-xs text-gray-500">Kolajda bu eşleşme varsayılan olarak korunur (ürün rengi ↔ değer).</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr><th className="px-3 py-2">{rec.isGarment !== false ? "Tişört" : "Ürün"}</th>{buyerLayers.map((l) => <th key={l.key} className="px-3 py-2">{l.labelTr}</th>)}</tr>
              </thead>
              <tbody>
                {full.items.map((it, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-900">{it.productColor} <span className="text-xs text-gray-500">({rec.isGarment !== false ? TONE_LABEL[it.tone].toLowerCase() : ""}{it.sides.length > 1 ? `, ${it.sides.map((s) => SIDE_LABEL[s].toLowerCase()).join(" + ")}` : ""})</span></td>
                    {buyerLayers.map((l) => <td key={l.key} className="px-3 py-2 text-gray-700">{it.values[l.key] ?? "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alicinin gorecegi (Etsy kisisellestirme kutusu) */}
      <div>
        <h3 className="mb-1 text-sm font-semibold text-gray-700">Alıcının Etsy'de göreceği</h3>
        {full.questionStyleTr && <p className="mb-2 text-xs text-gray-500">{full.questionStyleTr}</p>}
        <div className="max-w-md space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          {full.questions.length === 0 && <p className="text-sm text-gray-500">Kişiselleştirme sorusu yok.</p>}
          {full.questions.map((q, i) => (
            <div key={i}>
              <p className="text-sm font-semibold text-gray-900">{q.question}{q.required ? "" : <span className="font-normal text-gray-500"> (optional)</span>}</p>
              {q.instructions && <p className="mt-0.5 whitespace-pre-line text-xs text-gray-600">{q.instructions}</p>}
              {q.type === "text_input" && (
                <div className="mt-1.5 flex h-16 items-end justify-end rounded-lg border border-gray-300 p-1.5 text-xs text-gray-400">{q.maxChars ? `0/${q.maxChars}` : ""}</div>
              )}
              {q.type === "dropdown" && (
                <select disabled className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
                  <option>Select an option</option>
                  {q.options?.map((o) => <option key={o}>{o}</option>)}
                </select>
              )}
              {q.type.endsWith("upload") && (
                <div className="mt-1.5 inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1.5 text-xs text-gray-700"><Upload className="h-3.5 w-3.5" /> Upload{q.maxFiles ? ` (max ${q.maxFiles})` : ""}</div>
              )}
              {q.instructions && q.instructions.length > 120 && <p className="mt-1 text-xs text-red-600">Talimat {q.instructions.length} karakter; Etsy 120'de keser.</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Uretilecek kartelalar */}
      {full.charts.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-700">Listeye gereken kartelalar</h3>
          <ul className="space-y-1 text-sm text-gray-700">
            {full.charts.map((c, i) => <li key={i}><span className="font-medium text-gray-900">{c.titleTr}</span>: {c.purposeTr}</li>)}
          </ul>
        </div>
      )}

      {full.warningsTr.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Dikkat</p>
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-amber-700">{full.warningsTr.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}

      {/* Onay / duzeltme */}
      <div className="space-y-3 border-t border-gray-100 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          {approved ? (
            <>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Onaylandı</span>
              <button onClick={() => approve.mutate("draft")} disabled={approve.isPending}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"><RotateCcw className="h-3.5 w-3.5" /> Onayı kaldır</button>
            </>
          ) : (
            <button onClick={() => approve.mutate("approved")} disabled={approve.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {approve.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Doğru, onayla
            </button>
          )}
        </div>
        <div>
          <label htmlFor="study-feedback" className="mb-1 block text-xs font-medium text-gray-700">Yanlış olan bir şey varsa yaz</label>
          <textarea id="study-feedback" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3}
            placeholder="Örnek: Öğretmen adı da harf harf çizilmiş, font değil. / Sınıf seviyesi açılır menü olsun."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button onClick={() => run.mutate(feedback)} disabled={!feedback.trim() || run.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-indigo-600" />} Düzeltmeyle yeniden çalıştır
            </button>
            <span className="text-xs text-gray-500">Notundan genel bir kural çıkarsa ders olarak kaydedilir; sonraki listelerde aynı hata tekrarlanmaz.</span>
          </div>
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-xs font-medium text-gray-500">Model seç ve baştan çalıştır</summary>
          <div className="mt-2">{runner}</div>
        </details>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <p className="text-xs text-gray-400">Model: {full.model} · {new Date(full.analyzedAt).toLocaleString("tr-TR")}</p>
      </div>
    </div>
  );
}
